import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Shield, Cloud, Lock, ChevronRight, Activity, Eye, Server, FileSearch, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { loginWithPasskey } from "../lib/webauthn";

export default function Login() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [twofa, setTwofa] = useState(null); // { login_token, email }
  const [code, setCode] = useState("");
  const [ssoProviders, setSsoProviders] = useState([]);

  useEffect(() => {
    api.get("/sso/providers/public")
      .then(({ data }) => setSsoProviders(Array.isArray(data) ? data : []))
      .catch(() => setSsoProviders([]));
  }, []);

  // After SSO callback: if the IdP signed the user in but the org policy needs
  // an extra factor, the backend redirects here with ?step=mfa&login_token=...
  // Land directly in the TOTP prompt rather than show the password form again.
  useEffect(() => {
    const u = new URL(window.location.href);
    const step = u.searchParams.get("step");
    const lt = u.searchParams.get("login_token");
    const em = u.searchParams.get("email") || "";
    const reason = u.searchParams.get("reason");
    if (step === "mfa" && lt) {
      setTwofa({ login_token: lt, email: em });
      if (reason) setErr(`Step-up required: ${reason}`);
    } else if (step === "mfa_enroll" && lt) {
      setErr(`Your organization requires MFA, but no passkey or authenticator is enrolled for ${em || "this account"}. Contact an administrator.`);
    }
  }, []);

  const startSSO = async (slug) => {
    try {
      const { data } = await api.get(`/sso/oidc/${slug}/login`);
      if (data?.authorize_url) window.location.href = data.authorize_url;
      else setErr("SSO provider misconfigured (no authorize URL)");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const ssoLogo = (kind) => {
    if (kind === "google_workspace") return "G";
    if (kind === "azure_ad") return "M";
    if (kind === "okta") return "O";
    return "·";
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (mode === "login") {
        const data = await login(email, password);
        if (data?.requires_2fa) {
          setTwofa({ login_token: data.login_token, email: data.email });
          setBusy(false);
          return;
        }
        if (data?.requires_2fa_enrollment) {
          // Org policy demands MFA but this user has no TOTP enrolled.
          // Park them on a dedicated page; auth context isn't established yet
          // because we never received the access cookie.
          setErr(`Your organization requires MFA. Sign in via SSO or contact an administrator to enroll in TOTP. (reason: ${data.reason || "policy"})`);
          setBusy(false);
          return;
        }
      } else {
        await register(email, password, name || email.split("@")[0]);
      }
      toast.success("Authenticated");
      navigate("/dashboard");
    } catch (e2) {
      const msg = formatApiError(e2.response?.data?.detail) || e2.message;
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await (await import("../lib/api")).api.post("/auth/login/2fa", { login_token: twofa.login_token, code });
      // Refresh auth context
      const me = await (await import("../lib/api")).api.get("/auth/me");
      window.location.href = "/dashboard";
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-zinc-950">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden border-r border-zinc-800">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1683447551794-1c287cd42675?crop=entropy&cs=srgb&fm=jpg&w=1600)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-zinc-950/80" />
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <img src="/rustwall-logo.png" alt="Rustwall" className="h-12 w-auto object-contain" />
          </div>
        </div>
        <div className="relative z-10 space-y-6">
          <h1 className="font-display text-5xl xl:text-6xl font-black tracking-tight leading-[1.05]">
            Multi-cloud<br />
            firewall control,<br />
            <span className="text-cyan-400">re-architected in Rust.</span>
          </h1>
          <p className="text-zinc-400 max-w-md font-mono text-xs leading-relaxed">
            POSTURE // RUNTIME // POLICY // DLP // PROXY // AUDIT
            <br />
            UNIFIED ACROSS AWS · AZURE · GCP · ORACLE
          </p>
          <div className="grid grid-cols-2 gap-3 max-w-md pt-4">
            {[
              { i: <Cloud className="w-4 h-4" />, t: "Multi-cloud posture", s: "CSPM across 4 providers" },
              { i: <Shield className="w-4 h-4" />, t: "Runtime detection", s: "Live threat surfacing" },
              { i: <Lock className="w-4 h-4" />, t: "Zero-trust policy", s: "Identity-aware enforcement" },
              { i: <FileSearch className="w-4 h-4" />, t: "DLP + labels", s: "Inline scan, label-aware rules" },
              { i: <Server className="w-4 h-4" />, t: "Web proxy filter", s: "URL allow / deny, TLS" },
              { i: <Activity className="w-4 h-4" />, t: "Self-healing", s: "Auto-remediation flows" },
              { i: <Eye className="w-4 h-4" />, t: "Audit telemetry", s: "Investigable event log" },
              { i: <ChevronRight className="w-4 h-4" />, t: "Compliance maps", s: "SOC 2 · NIST · PCI · HIPAA · CIS" },
            ].map((x) => (
              <div key={x.t} className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-3 flex items-start gap-2">
                <div className="text-cyan-400 pt-0.5">{x.i}</div>
                <div className="min-w-0">
                  <div className="text-xs font-mono text-zinc-200 leading-tight">{x.t}</div>
                  <div className="text-[10px] font-mono text-zinc-500 leading-tight mt-0.5">{x.s}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-600 max-w-md pt-1">
            ROADMAP · FIPS-validated crypto · PHI / HIPAA enforcement · gov-cloud · AI traffic guardrails
          </div>
        </div>
        <div className="relative z-10 flex items-center gap-2 text-[10px] font-mono text-zinc-500 uppercase tracking-[0.2em]">
          <span className="live-dot" /> SYSTEM ONLINE · NODE 0xA1F · v3.2.1
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md space-y-6 fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-2">
            <img src="/rustwall-logo.png" alt="Rustwall" className="h-10 w-auto object-contain" />
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400 mb-3" data-testid="login-stage">
              {mode === "login" ? "// SECURE ACCESS" : "// PROVISION OPERATOR"}
            </div>
            <h2 className="font-display text-3xl font-bold tracking-tight">
              {mode === "login" ? "Sign in to continue" : "Create operator"}
            </h2>
            <p className="text-zinc-400 text-sm mt-2">
              {mode === "login" ? "Sign in with your operator credentials." : "Default role is analyst. Admins can elevate roles."}
            </p>
          </div>

          {!twofa && (
            <div className="space-y-2" data-testid="sso-buttons">
              <button
                type="button"
                data-testid="passkey-btn"
                onClick={async () => {
                  setErr("");
                  if (!email) { setErr("Enter your email first, then press Sign in with passkey."); return; }
                  if (!window.PublicKeyCredential) { setErr("This browser doesn't support WebAuthn."); return; }
                  setBusy(true);
                  try {
                    await loginWithPasskey(email);
                    window.location.href = "/dashboard";
                  } catch (e2) {
                    setErr(formatApiError(e2.response?.data?.detail) || e2.message || "Passkey login failed");
                  } finally { setBusy(false); }
                }}
                className="w-full h-11 flex items-center justify-center gap-3 border border-zinc-700 hover:border-cyan-500/60 hover:bg-zinc-900 rounded-sm text-sm font-mono transition-colors"
              >
                <span className="w-5 h-5 grid place-items-center rounded-sm bg-zinc-800 text-cyan-400">
                  <KeyRound className="w-3 h-3" />
                </span>
                <span>Sign in with passkey</span>
              </button>
              {ssoProviders.map((p) => (
                <button
                  key={p.slug}
                  type="button"
                  data-testid={`sso-btn-${p.slug}`}
                  onClick={() => startSSO(p.slug)}
                  className="w-full h-11 flex items-center justify-center gap-3 border border-zinc-700 hover:border-cyan-500/60 hover:bg-zinc-900 rounded-sm text-sm font-mono transition-colors"
                >
                  <span className="w-5 h-5 grid place-items-center rounded-sm bg-zinc-800 text-cyan-400 text-xs font-bold">
                    {ssoLogo(p.kind)}
                  </span>
                  <span>Continue with {p.name}</span>
                </button>
              ))}
              <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-600 pt-1">
                <span className="flex-1 h-px bg-zinc-800" />
                <span>or</span>
                <span className="flex-1 h-px bg-zinc-800" />
              </div>
            </div>
          )}
          <form onSubmit={twofa ? submit2fa : submit} className="space-y-4" data-testid="auth-form">
            {twofa ? (
              <>
                <div className="border border-cyan-500/30 bg-cyan-500/5 rounded-sm p-3 text-xs font-mono text-cyan-300" data-testid="2fa-prompt">
                  Enter the 6-digit code from your authenticator app for {twofa.email}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-widest text-zinc-400 font-mono">TOTP Code</Label>
                  <Input
                    autoFocus required maxLength={6} data-testid="login-2fa-code"
                    value={code} onChange={(e) => setCode(e.target.value)}
                    className="bg-zinc-900 border-zinc-800 focus:border-cyan-500 rounded-sm h-12 font-mono text-2xl tracking-[0.5em] text-center"
                    placeholder="000000"
                  />
                </div>
              </>
            ) : (
              <>
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-[11px] uppercase tracking-widest text-zinc-400 font-mono">Name</Label>
                <Input
                  id="name" data-testid="name-input"
                  value={name} onChange={(e) => setName(e.target.value)}
                  className="bg-zinc-900 border-zinc-800 focus:border-cyan-500 rounded-sm h-11"
                  placeholder="Operator name"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-[11px] uppercase tracking-widest text-zinc-400 font-mono">Email</Label>
              <Input
                id="email" type="email" required data-testid="email-input"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="bg-zinc-900 border-zinc-800 focus:border-cyan-500 rounded-sm h-11 font-mono text-sm"
                placeholder="admin@rustwall.io"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-[11px] uppercase tracking-widest text-zinc-400 font-mono">Password</Label>
              <Input
                id="password" type="password" required data-testid="password-input"
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="bg-zinc-900 border-zinc-800 focus:border-cyan-500 rounded-sm h-11 font-mono text-sm"
                placeholder="••••••••"
              />
            </div>
              </>
            )}
            {err && (
              <div className="text-xs text-red-400 font-mono border border-red-500/30 bg-red-500/5 p-3 rounded-sm" data-testid="auth-error">
                ⚠ {err}
              </div>
            )}
            <Button
              type="submit" disabled={busy} data-testid="submit-auth-btn"
              className="w-full h-11 bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm font-semibold tracking-wide gap-2"
            >
              {busy ? "..." : twofa ? "VERIFY CODE" : mode === "login" ? "AUTHENTICATE" : "CREATE ACCOUNT"} <ChevronRight className="w-4 h-4" />
            </Button>
          </form>

          <div className="text-center text-xs text-zinc-500">
            {mode === "login" ? "No account?" : "Already have one?"}{" "}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }}
              className="text-cyan-400 hover:text-cyan-300 underline-offset-2 hover:underline"
            >
              {mode === "login" ? "Register operator" : "Sign in"}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
